import React from "react";
import "./WhyChooseUs.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faChartPie,
  faUserShield,
  faScaleBalanced,
  faHeadset,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const cards = [
  {
    icon: faFileContract,
    title: "Scoped in writing",
    text: "Proposals list deliverables, fees, and assumptions. Change orders happen when scope shifts—not through surprise invoices.",
  },
  {
    icon: faChartPie,
    title: "Reporting you can use",
    text: "Dashboards and summaries connect spend to outcomes you care about, with caveats when data is modeled or incomplete.",
  },
  {
    icon: faUserShield,
    title: "Privacy-aware setup",
    text: "Tags, pixels, and consent tools are configured with your privacy policy and legal guidance in mind—not “track everything by default.”",
  },
  {
    icon: faScaleBalanced,
    title: "Honest claims",
    text: "We help you avoid exaggerated promises in ads and landing pages—better for customers and for platform approvals.",
  },
  {
    icon: faHeadset,
    title: "Direct communication",
    text: "You get a clear point of contact and regular touchpoints. No black-box “trust us” marketing.",
  },
  {
    icon: faCircleCheck,
    title: "Corrections welcome",
    text: "If copy, targeting, or tracking drifts out of date, we fix it and document what changed.",
  },
];

const WhyChooseUs = () => {
  return (
    <section className="why" id="why-choose" aria-label="Why work with us">
      <div className="why__wrap">
        <div className="why__left">
          <motion.p
            className="why__kicker"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
          >
            Why clients stay
          </motion.p>

          <motion.h2
            className="why__title"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            Marketing partners who <span className="why__accent">tell the truth</span>.
          </motion.h2>

          <motion.p
            className="why__sub"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.12 }}
          >
            We are built for teams that want growth without cutting corners on compliance or customer trust.
            We are not a substitute for your legal counsel or in-house brand team.
          </motion.p>

          <motion.ul
            className="why__pillars"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.55, delay: 0.2 }}
          >
            <li>No fake case-study metrics</li>
            <li>No “guaranteed” rankings or ROAS</li>
            <li>Clear disclosure when we earn referral fees</li>
          </motion.ul>
        </div>

        <div className="why__right">
          {cards.map((c, idx) => (
            <motion.article
              key={c.title}
              className="why__card"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.55, delay: idx * 0.06 }}
            >
              <div className="why__icon" aria-hidden="true">
                <FontAwesomeIcon icon={c.icon} />
              </div>

              <h3 className="why__cardTitle">{c.title}</h3>
              <p className="why__cardText">{c.text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
